/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @link      https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments2026.
 * @license   MIT
 * @description Player guide for current progression, economy, and survival systems.
 */

import React, { useState } from "react";
import {
  Activity,
  Book,
  Clock,
  Crown,
  DollarSign,
  ListChecks,
  ShieldCheck,
  Swords,
  Target,
  Users,
} from "lucide-react";

const GAME_RULES = {
  maxLevel: 150,
  maxTurns: 100,
  tickTurns: 5,
  dailyCash: 1000,
  dailyTurns: 15,
  allianceLevel: 10,
  allianceCost: 100000,
  maxDistricts: 3,
};

const CHAPTERS = [
  { id: "START", icon: ListChecks, label: "Start Here" },
  { id: "TURNS", icon: Clock, label: "Turns & Ticks" },
  { id: "MONEY", icon: DollarSign, label: "Making Money" },
  { id: "CREW", icon: Users, label: "Crew & Production" },
  { id: "SURVIVAL", icon: Activity, label: "Survival & Heat" },
  { id: "COMBAT", icon: Swords, label: "Combat" },
  { id: "ALLIANCES", icon: ShieldCheck, label: "Alliances & Turf" },
  { id: "PRESTIGE", icon: Crown, label: "Prestige" },
  { id: "REFERENCE", icon: Target, label: "Quick Reference" },
];

const TONE_STYLES = {
  green: "border-green-900/60 bg-green-900/10",
  blue: "border-blue-900/60 bg-blue-900/10",
  orange: "border-orange-900/60 bg-orange-900/10",
  red: "border-red-900/60 bg-red-900/10",
  purple: "border-purple-900/60 bg-purple-900/10",
  zinc: "border-zinc-800 bg-zinc-900/50",
};

const GuideCard = ({ title, tone = "zinc", children }) => (
  <div className={`border p-4 ${TONE_STYLES[tone]}`}>
    <h4 className="mb-2 text-xs font-black uppercase text-white">{title}</h4>
    <div className="text-[11px] leading-relaxed text-zinc-400">{children}</div>
  </div>
);

const GuideList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item) => (
      <li key={item} className="flex gap-2">
        <span className="font-black text-green-500">•</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const NavPath = ({ children }) => (
  <span className="font-black text-white">{children}</span>
);

const ChapterTitle = ({ icon: Icon, children, color = "text-green-500" }) => (
  <h3 className="mb-6 flex items-center gap-4 text-3xl font-black uppercase italic text-white">
    <span className="border border-zinc-800 bg-zinc-900 p-3">
      <Icon className={color} size={32} />
    </span>
    {children}
  </h3>
);

const CurrentStatus = ({ stats }) => {
  const values = [
    ["Level", stats?.level ?? 1],
    ["Turns", stats?.turns ?? 0],
    ["Cash", `$${Number(stats?.cash || 0).toLocaleString()}`],
    ["Heat", stats?.heat_level ?? 0],
  ];

  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      {values.map(([label, value]) => (
        <div key={label} className="border border-zinc-800 bg-black p-3">
          <p className="text-[9px] font-black uppercase text-zinc-600">{label}</p>
          <p className="mt-1 text-sm font-black text-white">{value}</p>
        </div>
      ))}
    </div>
  );
};

export default function FieldManual({ stats }) {
  const [activeChapter, setActiveChapter] = useState("START");

  return (
    <div className="flex min-h-[750px] flex-col border-2 border-zinc-800 bg-zinc-950 font-mono shadow-[0_0_50px_rgba(0,0,0,1)] md:flex-row">
      <div className="w-full space-y-2 border-b border-zinc-800 bg-black/80 p-4 md:w-64 md:border-b-0 md:border-r">
        <div className="mb-6 px-2">
          <h2 className="flex items-center gap-2 font-black uppercase italic text-white">
            <Book className="text-green-500" /> Field Manual
          </h2>
          <p className="text-[9px] uppercase tracking-tighter text-zinc-600">
            Current player guide
          </p>
        </div>

        {CHAPTERS.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveChapter(id)}
            aria-pressed={activeChapter === id}
            className={`flex w-full items-center gap-3 border px-4 py-3 text-left text-[10px] font-black uppercase italic transition-all ${
              activeChapter === id
                ? "border-green-400 bg-green-600 text-black shadow-[0_0_20px_rgba(34,197,94,0.2)]"
                : "border-transparent text-zinc-500 hover:bg-zinc-900 hover:text-white"
            }`}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      <div className="max-h-[750px] flex-1 space-y-8 overflow-y-auto bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-zinc-900/20 via-transparent to-transparent p-5 md:p-8">
        {activeChapter === "START" && (
          <div className="animate-in slide-in-from-bottom-2 duration-500">
            <ChapterTitle icon={ListChecks}>What To Do First</ChapterTitle>
            <p className="mb-6 text-sm leading-relaxed text-zinc-400">
              The Field Manual is your in-game guide. Use it whenever you need to
              know what a system does, where to find it, or what your next move
              should be.
            </p>

            <CurrentStatus stats={stats} />

            <div className="mt-6 grid gap-4">
              {[
                [
                  "1. Claim your daily package",
                  "Use the Daily Claim button when it is ready. The standard reward is $1,000 and 15 turns.",
                ],
                [
                  "2. Earn starting cash and XP",
                  "Open SYNDICATE > CITY, select a district, and choose a crime that matches your level and available turns.",
                ],
                [
                  "3. Recruit your first muscle",
                  "Open HOME > EMPIRE and scout thugs. Thugs protect your operation and provide combat power.",
                ],
                [
                  "4. Start producing",
                  "Open SYNDICATE > OPERATIONS > COOKBOOK. Unlock The Reggie for free, then spend 1 turn to cook a batch.",
                ],
                [
                  "5. Sell your first batch",
                  "Open SYNDICATE > OPERATIONS > DROPS. Manual sales cost 1 turn, create heat, and can be busted.",
                ],
                [
                  "6. Protect your cash",
                  "Open HOME > SAFE and deposit money you do not need immediately. Banked cash is protected from combat theft and survival collapse.",
                ],
                [
                  "7. Automate the operation",
                  "Recruit cooks and runners, then assign them to recipes in the Warehouse. Cooks produce stash and runners sell it on engine ticks.",
                ],
              ].map(([title, description]) => (
                <div
                  key={title}
                  className="border-l-4 border-green-600 bg-zinc-900 p-4"
                >
                  <h4 className="text-xs font-black uppercase text-white">
                    {title}
                  </h4>
                  <p className="mt-1 text-[11px] leading-relaxed text-zinc-400">
                    {description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeChapter === "TURNS" && (
          <div className="animate-in slide-in-from-bottom-2 duration-500">
            <ChapterTitle icon={Clock} color="text-blue-500">
              Turns & Engine Ticks
            </ChapterTitle>
            <div className="grid gap-4 md:grid-cols-2">
              <GuideCard title="How turns return" tone="blue">
                <GuideList
                  items={[
                    `Each processed engine tick gives 5 turns, up to the normal ${GAME_RULES.maxTurns}-turn cap.`,
                    "The engine does not run at one fixed interval. Ticks are faster during peak hours and slower overnight.",
                    `The standard daily claim gives ${GAME_RULES.dailyTurns} turns and $${GAME_RULES.dailyCash.toLocaleString()}.`,
                    "Premium turn conversions can temporarily take a player above the normal regeneration cap.",
                  ]}
                />
              </GuideCard>
              <GuideCard title="Engine timing (UTC)" tone="zinc">
                <GuideList
                  items={[
                    "02:00-08:00: approximately every 25-45 minutes.",
                    "16:00-23:00: approximately every 8-15 minutes.",
                    "All other hours: approximately every 12-25 minutes.",
                    "Only recently active players are processed.",
                  ]}
                />
              </GuideCard>
            </div>

            <div className="mt-4 overflow-hidden border border-zinc-800">
              {[
                ["Cook one batch", "1"],
                ["Sell product manually", "1"],
                ["Scout runner", "2"],
                ["Scout girl", "2"],
                ["Scout thug", "3"],
                ["Scout cook", "5"],
                ["Attack a player or rival", "2"],
                ["Challenge a district", "10"],
                ["Rename an alliance", "25"],
                ["Change an alliance tag", "50"],
              ].map(([action, cost], index) => (
                <div
                  key={action}
                  className={`flex items-center justify-between px-4 py-2 text-[11px] ${
                    index % 2 === 0 ? "bg-zinc-900/70" : "bg-black"
                  }`}
                >
                  <span className="text-zinc-400">{action}</span>
                  <span className="font-black text-white">{cost} turns</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeChapter === "MONEY" && (
          <div className="animate-in slide-in-from-bottom-2 duration-500">
            <ChapterTitle icon={DollarSign}>Making & Protecting Money</ChapterTitle>
            <div className="grid gap-4 md:grid-cols-2">
              <GuideCard title="Crimes" tone="purple">
                Use <NavPath>SYNDICATE &gt; CITY</NavPath>. Successful crimes pay
                random cash and XP within the displayed range. Failure still
                spends the listed turns and adds twice the normal heat.
              </GuideCard>
              <GuideCard title="Product sales" tone="green">
                Cook product, then sell it through <NavPath>DROPS</NavPath>.
                Higher-skill products require at least one runner in your
                inventory before they can be sold manually.
              </GuideCard>
              <GuideCard title="Passive income" tone="blue">
                Assigned cooks produce product, assigned runners sell stash,
                girls generate protected-operation income, businesses pay cash
                and XP, and controlled districts pay alliance members on ticks.
              </GuideCard>
              <GuideCard title="Businesses" tone="orange">
                Businesses can be purchased in city districts and upgraded.
                Assigned guards add 5% cash income each. A business operating in
                another alliance&apos;s district pays 60% tax to that alliance.
              </GuideCard>
              <GuideCard title="The Safe" tone="zinc">
                Use <NavPath>HOME &gt; SAFE</NavPath> to protect liquid cash.
                Deposits and withdrawals are free. Transfers to accepted friends
                cost a 2% fee and create 1 heat per $100,000 sent.
              </GuideCard>
              <GuideCard title="What remains vulnerable" tone="red">
                Liquid cash can be stolen in combat, seized in raids, or wiped
                by survival collapse. Unsold product and active units can also
                be lost. Bank important cash instead of carrying everything.
              </GuideCard>
            </div>
          </div>
        )}

        {activeChapter === "CREW" && (
          <div className="animate-in slide-in-from-bottom-2 duration-500">
            <ChapterTitle icon={Users} color="text-blue-500">
              Crew & Production
            </ChapterTitle>
            <div className="space-y-4">
              {[
                [
                  "Thugs",
                  "3 turns per scouting attempt. Recruitment starts at an 80% chance and becomes harder as your thug count grows, down to a 20% minimum. Thugs provide combat power, protect girls, and can guard businesses.",
                  "red",
                ],
                [
                  "Girls",
                  "2 turns per scouting attempt with a 30% success chance. Their tick income depends on protection: armed thugs count fully and unarmed thugs count at half strength. Poor protection lowers happiness and increases raid danger.",
                  "purple",
                ],
                [
                  "Runners",
                  "2 turns per scouting attempt. Every completed attempt recruits a runner. Assign runners to a recipe in the Warehouse to sell part of that stash automatically on ticks.",
                  "green",
                ],
                [
                  "Cooks",
                  "5 turns per scouting attempt with a 40% success chance. Assign cooks to unlocked recipes in the Warehouse so they produce stash automatically on ticks.",
                  "blue",
                ],
                [
                  "Guard Dogs",
                  "Dogs are bought from the market. They add combat power and can reduce casualties or gear-loss risk when fights go badly.",
                  "orange",
                ],
              ].map(([title, description, tone]) => (
                <GuideCard key={title} title={title} tone={tone}>
                  {description}
                </GuideCard>
              ))}
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <GuideCard title="Manual production">
                Cooking costs 1 turn and awards the recipe&apos;s XP. The
                displayed yield is random, but the stored maximum is an upper
                boundary and is not rolled by the current production formula.
              </GuideCard>
              <GuideCard title="Warehouse automation">
                Assign cooks and runners separately. Cooks add product to the
                selected recipe stash; runners sell from their selected recipe.
                Runner movement is capped at 50% of the assigned stash per tick.
              </GuideCard>
            </div>
          </div>
        )}

        {activeChapter === "SURVIVAL" && (
          <div className="animate-in slide-in-from-bottom-2 duration-500">
            <ChapterTitle icon={Activity} color="text-red-500">
              Survival, Heat & Raids
            </ChapterTitle>
            <div className="grid gap-4 md:grid-cols-2">
              <GuideCard title="Food, water and health" tone="red">
                <GuideList
                  items={[
                    "Hunger falls by 1 and thirst falls by 2 per processed tick while alive.",
                    "At zero hunger, health falls by 2 per tick. At zero thirst, health falls by 4 per tick. These penalties stack.",
                    "If health reaches zero, liquid cash is lost and the player enters a 30-minute hospital recovery.",
                    "Buy food and drinks at TRADER CITY > SEVEN ELEVEN and consume them from HOME > SURVIVAL or ITEMS.",
                  ]}
                />
              </GuideCard>
              <GuideCard title="Heat management" tone="orange">
                <GuideList
                  items={[
                    "Successful manual product sales add 2 heat.",
                    "Crime failures add twice the crime's listed heat.",
                    "Heat falls by 5 points for each processed tick.",
                    "A police bribe removes up to 20 heat at $2,500 per point.",
                  ]}
                />
              </GuideCard>
              <GuideCard title="Manual-sale busts" tone="red">
                The bust chance starts at 2% and rises with heat, reaching 15%
                at 100 heat. A bust destroys the entire amount offered for sale,
                removes at least one runner, and still spends the sale turn.
              </GuideCard>
              <GuideCard title="Federal raids" tone="orange">
                Large recent passive income, high heat, and unhappy girls
                increase raid exposure. Raids can seize liquid cash, stash,
                girls, runners, cooks, thugs, and high-end weapons. Good
                protection and lower heat reduce the risk.
              </GuideCard>
            </div>
          </div>
        )}

        {activeChapter === "COMBAT" && (
          <div className="animate-in slide-in-from-bottom-2 duration-500">
            <ChapterTitle icon={Swords} color="text-red-600">
              Street Combat
            </ChapterTitle>
            <div className="grid gap-4 md:grid-cols-2">
              <GuideCard title="Building power" tone="red">
                Combat power comes from level, thugs, dogs, item rarity, assigned
                weapons and armor, and gear durability. Damaged equipment gives
                less power, so repair useful gear from <NavPath>HOME &gt; ITEMS</NavPath>.
              </GuideCard>
              <GuideCard title="Before attacking" tone="orange">
                A standard attack costs 2 turns. If you have girls, you need
                more thugs than girls before enough muscle is available to
                attack. Bank spare cash and expect losses when taking close
                fights.
              </GuideCard>
              <GuideCard title="Winning a fight" tone="green">
                A player victory awards 25 XP and can steal 2% of the
                defender&apos;s liquid cash, capped at $100,000, plus 15% of each
                unsold product stash. Eligible bounties are paid as well.
              </GuideCard>
              <GuideCard title="Losing an attack" tone="red">
                The attacking player can lose units and may lose an unprotected
                weapon. Dogs can modify casualty and gear-loss outcomes. Items
                marked as protected are excluded from these losses.
              </GuideCard>
            </div>

            <GuideCard title="Outnumbering bonus" tone="zinc">
              Attack power receives a 1.5x multiplier at 2-to-1 units, 2x at
              5-to-1, and 3x at 10-to-1. A 10x power advantage gives a 99% win
              chance; a 50x advantage guarantees the attacking win.
            </GuideCard>
          </div>
        )}

        {activeChapter === "ALLIANCES" && (
          <div className="animate-in slide-in-from-bottom-2 duration-500">
            <ChapterTitle icon={ShieldCheck} color="text-blue-500">
              Alliances & Territory
            </ChapterTitle>
            <div className="grid gap-4 md:grid-cols-2">
              <GuideCard title="Join or create" tone="blue">
                Players can request to join an existing alliance. Creating one
                requires Level {GAME_RULES.allianceLevel} and $
                {GAME_RULES.allianceCost.toLocaleString()}. The leader approves
                join requests.
              </GuideCard>
              <GuideCard title="District warfare" tone="red">
                District challenges use the combined inventory and average
                level of the alliance, not one player&apos;s normal PvP stats.
                Challenges require 10 turns, and an alliance can control up to{" "}
                {GAME_RULES.maxDistricts} districts.
              </GuideCard>
              <GuideCard title="Territory income" tone="green">
                Controlled districts pay their listed cash bonus to alliance
                members on processed ticks. Territory also collects the 60%
                foreign-control tax from businesses owned by outsiders.
              </GuideCard>
              <GuideCard title="Treasury and HQ" tone="purple">
                Members can donate cash to the treasury. Leaders spend $500,000
                from treasury funds to add 5 member slots and raise alliance
                level. Completed alliance missions also pay the treasury.
              </GuideCard>
              <GuideCard title="Identity changes" tone="orange">
                Renaming costs $250,000 and 25 turns. Changing the tag costs
                $500,000 and 50 turns. Each change has its own 7-day cooldown.
              </GuideCard>
            </div>
          </div>
        )}

        {activeChapter === "PRESTIGE" && (
          <div className="animate-in slide-in-from-bottom-2 duration-500">
            <ChapterTitle icon={Crown} color="text-yellow-500">
              Prestige & Recognition
            </ChapterTitle>
            <div className="grid gap-4 md:grid-cols-2">
              <GuideCard title="Service Medals" tone="green">
                Reach Level {GAME_RULES.maxLevel}, then use the Service Medal
                claim on your profile. Claiming resets your level to 1 and XP to
                0, and awards the next medal tier for the current year.
              </GuideCard>
              <GuideCard title="Annual medal progression" tone="blue">
                There are six yearly tiers. Your new current-year medal replaces
                the lower current-year tier in inventory. Tier 6 is the highest
                medal available for that year.
              </GuideCard>
              <GuideCard title="What prestige keeps" tone="zinc">
                The prestige claim resets level and XP only. Your cash, bank
                balance, inventory, stash, units, businesses, and social
                progress are not reset by the medal claim.
              </GuideCard>
              <GuideCard title="Achievements & veteran coins" tone="purple">
                Achievements award XP when their tracked goal is completed.
                Veteran collectibles recognize account-age milestones at 1, 2,
                3, 5, and 10 years.
              </GuideCard>
            </div>
          </div>
        )}

        {activeChapter === "REFERENCE" && (
          <div className="animate-in slide-in-from-bottom-2 duration-500">
            <ChapterTitle icon={Target}>Quick Reference</ChapterTitle>
            <div className="grid gap-4 md:grid-cols-2">
              <GuideCard title="Main navigation">
                <GuideList
                  items={[
                    "HOME: empire overview, survival, inventory, bank, friends, messages, and achievements.",
                    "TRADER CITY: equipment, food and drink, and the credit exchange. Credit purchasing is under construction.",
                    "SYNDICATE: crimes, businesses, production, combat, targets, rankings, alliances, and history.",
                    "SYSTEM: Field Manual, changelog, settings, and staff tools where permitted.",
                  ]}
                />
              </GuideCard>
              <GuideCard title="Important caps">
                <GuideList
                  items={[
                    `Maximum player level: ${GAME_RULES.maxLevel}.`,
                    `Normal regenerated turn cap: ${GAME_RULES.maxTurns}.`,
                    `Maximum controlled districts per alliance: ${GAME_RULES.maxDistricts}.`,
                    "Heat is capped between 0 and 100.",
                    "Runner auto-sales move at most 50% of a recipe stash per processed tick.",
                  ]}
                />
              </GuideCard>
              <GuideCard title="Safest routine" tone="green">
                Claim daily rewards, keep survival supplies ready, spend turns
                before reaching the cap, bank cash you do not need, keep heat
                manageable, protect girls with thugs and weapons, and automate
                recipes with assigned cooks and runners.
              </GuideCard>
              <GuideCard title="Need more help?" tone="blue">
                Check the Changelog for recent updates. If a displayed value or
                action does not match this manual, report it to staff instead of
                trying to exploit the difference.
              </GuideCard>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
